import { MetricsRequest, MetricsResponse, HeagleRenderer, WebSocketUrlOption } from '../types';

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectInterval: NodeJS.Timeout | null = null;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private connectionTimeoutMs = 10000; // 10초 타임아웃
  private wsUrl: WebSocketUrlOption = 'wss://ssr-dev.conworth.net/webrtc/server';
  private onDataCallback: ((data: HeagleRenderer[]) => void) | null = null;
  private onErrorCallback: ((error: string) => void) | null = null;
  private onConnectionStatusCallback: ((connected: boolean) => void) | null = null;

  connect(
    onData: (data: HeagleRenderer[]) => void,
    onError: (error: string) => void,
    onConnectionStatus?: (connected: boolean) => void,
    wsUrl?: WebSocketUrlOption
  ) {
    this.onDataCallback = onData;
    this.onErrorCallback = onError;
    this.onConnectionStatusCallback = onConnectionStatus || null;
    if (wsUrl) {
      this.wsUrl = wsUrl;
    }
    this.connectWebSocket();
  }

  private connectWebSocket() {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;

    try {
      console.log('웹소켓 연결 시도 중...', this.wsUrl);
      console.log('현재 페이지 프로토콜:', window.location.protocol);
      console.log('현재 페이지 호스트:', window.location.host);

      this.ws = new WebSocket(this.wsUrl);
      
      // 연결 타임아웃 설정
      this.connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.log('웹소켓 연결 타임아웃');
          this.ws.close();
          this.onErrorCallback?.('웹소켓 연결 타임아웃이 발생했습니다.');
        }
      }, this.connectionTimeoutMs);
      
      this.ws.onopen = () => {
        console.log('웹소켓 연결됨');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.clearReconnectInterval();
        this.clearConnectionTimeout();
        this.onConnectionStatusCallback?.(true);
        this.requestMetrics();
      };

      this.ws.onmessage = (event) => {
        try {
          const response: MetricsResponse = JSON.parse(event.data);
          if (response.event === 'metrics' && response.stats) {
            const heagleRenderers = this.processMetricsData(response);
            this.onDataCallback?.(heagleRenderers);
          }
        } catch (error) {
          console.error('메시지 파싱 오류:', error);
          this.onErrorCallback?.('데이터 파싱 오류가 발생했습니다.');
        }
      };

      this.ws.onclose = (event) => {
        console.log('웹소켓 연결 종료됨:', event.code, event.reason);
        this.isConnecting = false;
        this.onConnectionStatusCallback?.(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('웹소켓 오류:', error);
        console.error('WebSocket readyState:', this.ws?.readyState);
        console.error('에러 발생 시간:', new Date().toISOString());

        this.isConnecting = false;
        this.onConnectionStatusCallback?.(false);

        // 더 상세한 에러 메시지
        let errorMessage = '웹소켓 연결 오류가 발생했습니다.';
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          errorMessage = '웹소켓 서버에 연결할 수 없습니다. 서버가 실행 중인지, 네트워크 연결이 정상인지 확인해주세요.';
        } else if (this.reconnectAttempts > 0) {
          errorMessage = `웹소켓 재연결 시도 중... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`;
        }

        this.onErrorCallback?.(errorMessage);
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('웹소켓 연결 실패:', error);
      this.isConnecting = false;
      this.onErrorCallback?.('웹소켓 연결에 실패했습니다.');
      this.scheduleReconnect();
    }
  }

  private requestMetrics() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const request: MetricsRequest = {
      ver: "1.3.0",
      ts: Date.now(),
      seq: 0,
      event: "metrics",
      sender: "client"
    };

    this.ws.send(JSON.stringify(request));
  }

  private processMetricsData(response: MetricsResponse): HeagleRenderer[] {
    const heagleRenderers: HeagleRenderer[] = [];
    
    response.stats.renderers.forEach(renderer => {
      let clientCount = 0;
      renderer.session_list.forEach(session => {
        clientCount += session.viewer_list.length;
      });

      heagleRenderers.push({
        renderer_id: renderer.renderer_id,
        renderer_group: renderer.renderer_group || '',
        renderer_name: renderer.renderer_name,
        renderer_port: renderer.renderer_port,
        max_conn: renderer.max_conn,
        client_count: clientCount
      });
    });

    return heagleRenderers;
  }

  private scheduleReconnect() {
    if (this.reconnectInterval || this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('최대 재연결 시도 횟수에 도달했습니다. 수동으로 재연결을 시도하세요.');
        this.onErrorCallback?.('웹소켓 연결에 실패했습니다. 페이지를 새로고침하거나 수동으로 재연결을 시도하세요.');
      }
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // 최대 30초

    console.log(`${delay/1000}초 후 재연결 시도 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.connectWebSocket();
    }, delay);
  }

  private clearReconnectInterval() {
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  private clearConnectionTimeout() {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  disconnect() {
    this.clearReconnectInterval();
    this.clearConnectionTimeout();
    this.reconnectAttempts = 0;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.onDataCallback = null;
    this.onErrorCallback = null;
    this.onConnectionStatusCallback = null;
  }

  requestData() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.requestMetrics();
    } else {
      console.log('웹소켓이 연결되지 않음. 데이터 요청을 건너뜀.');
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.ws) return 'DISCONNECTED';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }

  manualReconnect(wsUrl?: WebSocketUrlOption) {
    console.log('수동 재연결 시도...');
    if (wsUrl) {
      this.wsUrl = wsUrl;
    }
    this.reconnectAttempts = 0;
    this.clearReconnectInterval();
    this.connectWebSocket();
  }

  setWsUrl(wsUrl: WebSocketUrlOption) {
    this.wsUrl = wsUrl;
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;
